import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { formatMoney } from "../../lib/api";
import PriceDisplay from "../PriceDisplay";
import { Card } from "../ui/card";
import { Star, Users, Fuel, Cog, MapPin } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";

export const CAR_PLACEHOLDER = "/car-placeholder.svg";

export function carImageUrl(path) {
  if (!path) return CAR_PLACEHOLDER;
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
}

/** Use as onError handler on <img> tags rendering car images. */
export function carImgFallback(e) {
  if (e?.currentTarget && !e.currentTarget.dataset.fallback) {
    e.currentTarget.dataset.fallback = "1";
    e.currentTarget.src = CAR_PLACEHOLDER;
  }
}

export default function CarCard({ car, idx = 0 }) {
  const [searchParams] = useSearchParams();
  const dateQuery = (() => { const f = searchParams.get('from'); const u = searchParams.get('until'); if (!f && !u) return ''; const p = new URLSearchParams(); if (f) p.set('from', f); if (u) p.set('until', u); return '?' + p.toString(); })();
  const { currencies } = useCurrency();
  const ratingCount = car.rating_count ?? car.review_count ?? 0;
  const hasRating = car.rating && car.rating > 0;

  return (
    <Link to={`/cars/${car.id}${dateQuery}`} data-testid={`car-card-${car.id}`}>
      <Card
        className="overflow-hidden border-border bg-card shadow-organic hover:shadow-organic-hover transition-all duration-300 fade-up h-full flex flex-col group relative"
        style={{ animationDelay: `${idx * 60}ms` }}
      >
        {car.sponsored && (
          <div
            className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-[#E97A48] text-white text-[9px] sm:text-[10px] font-bold tracking-[0.12em] uppercase px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-md"
            data-testid={`sponsored-badge-${car.id}`}
          >
            Sponsored
          </div>
        )}
        <div className="aspect-[16/10] sm:aspect-[16/9] overflow-hidden bg-muted relative">
          <img
            src={carImageUrl(car.images?.[0])}
            alt={car.title}
            onError={carImgFallback}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-3 sm:p-5 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-display font-semibold tracking-tight text-sm sm:text-lg truncate">{car.title}</h3>
              <div className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <MapPin size={10} className="text-[#2A9D8F]" />
                <span className="truncate">{car.city}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-[11px] sm:text-xs text-foreground/70 shrink-0">
              <Star size={11} className="fill-[#E97A48] text-[#E97A48]" />
              {hasRating ? (
                <>
                  <span>{car.rating.toFixed(1)}</span>
                  {ratingCount > 0 && (
                    <span className="text-muted-foreground">({ratingCount})</span>
                  )}
                </>
              ) : (
                <span>New</span>
              )}
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-muted-foreground">
            {car.year && <span>{car.year}</span>}
            <span className="flex items-center gap-1"><Users size={10} /> {car.seats}</span>
            <span className="flex items-center gap-1"><Cog size={10} /> {car.transmission === "Automatic" ? "Auto" : "Manual"}</span>
            <span className="flex items-center gap-1"><Fuel size={10} /> {car.fuel === "Plugin-Hybrid" ? "PHEV" : car.fuel === "Electric" ? "EV" : car.fuel === "Hybrid" ? "Hybrid" : car.fuel}</span>
          </div>
          <div className="mt-3 sm:mt-4 pt-2.5 sm:pt-4 border-t border-border flex items-center justify-between">
            <div>
              <div className="font-display text-base sm:text-xl font-bold tracking-tight">
                <PriceDisplay amount={car.price_per_day} listingCurrency={car.currency} />
              </div>
              <div className="text-[10px] sm:text-xs text-muted-foreground -mt-0.5">per day</div>
            </div>
            <span className="px-2.5 sm:px-3 py-1 rounded-full bg-[#2A9D8F] text-white text-[10px] sm:text-xs font-semibold tracking-wide group-hover:bg-[#23887A] transition-colors">
              Book
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
